import React from "react";
import { motion } from "framer-motion";

function WhoItIsFor() {
  const list = [
    {
      title: "Angel Investors",
      desc: "Early backers seeking structured exposure to automated, high-conviction DeFi hedge fund strategies.",
    },
    {
      title: "Crypto-Native HNWIs",
      desc: "Investors familiar with digital assets who want institutional-grade automation and non-custodial execution.",
    },
    {
      title: "Professional Allocators",
      desc: "Family offices and funds requiring transparent reporting, automated performance tracking, and full custody control.",
    },
    {
      title: "Hands-Off Capital",
      desc: "Allocators who prefer proven, self-executing strategies without the operational complexity of trade management.",
    },
  ];

  return (
    <section
      id="who"
      className="relative py-28 bg-gradient-to-b from-[#050B1A] via-[#0A2A66] to-[#08183A] text-white overflow-hidden"
    >
      {/* Background subtle glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Animated mesh grid overlay */}
      <div className="absolute inset-0 -z-0 opacity-25 pointer-events-none">
        <svg
          className="w-full h-full animate-[floatgrid_60s_linear_infinite]"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern
              id="diagonalMesh"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="50"
                stroke="#ffffff"
                strokeOpacity="0.2"
                strokeWidth="2"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagonalMesh)" />
        </svg>
      </div>

      <style>
        {`
          @keyframes floatgrid {
            0% { transform: translate(0, 0); }
            50% { transform: translate(25px, 25px); }
            100% { transform: translate(0, 0); }
          }
        `}
      </style>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section label */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-xs tracking-wider font-semibold uppercase text-cyan-300 bg-white/10 px-4 py-1 rounded-full">
            Audience
          </span>
        </motion.div>

        {/* Heading */}
        <motion.h2
          className="text-4xl md:text-5xl font-bold text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Who It’s Built For
        </motion.h2>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-10">
          {list.map((item, idx) => (
            <motion.div
              key={idx}
              className="group relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl shadow-lg hover:shadow-xl p-8 transition-all overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
            >
              {/* Animated gradient line */}
              <div className="absolute top-0 left-0 w-0 h-[3px] bg-gradient-to-r from-cyan-400 to-blue-400 group-hover:w-full transition-all duration-500"></div>

              <h3 className="text-xl font-semibold mb-2 text-white">
                {item.title}
              </h3>
              <p className="text-gray-300 leading-relaxed">{item.desc}</p>

              {/* Hover effect glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-20 bg-gradient-to-r from-cyan-400 to-blue-500 blur-3xl transition-all duration-500"></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhoItIsFor;
